import { useMemo } from "react";

import {
  getAreReviewActionsDisabled,
  getMissingRequiredPcrsDetailsList,
  getShowMidMonthNotice,
  getShowNotOnPanelNotice,
  isPatientEnrolled,
} from "features/cdm/utils/cdmGating";
import { getCdmProgrammeOptions } from "features/cdm/utils/cdmProgrammeOptions";

// Derived CDM page state: the "Start review" programme options and the PCRS
// gating/notice flags, all memoized. Keeps the page free of derivation logic.
export const useCdmGating = ({
  doctor,
  cdmStatus,
  pcrsSchemeCheck,
  cdmPatientDetails,
  hasWsConnection,
  isPCRSLoading,
  isCdmPatientDetailsPending,
}) => {
  const options = useMemo(
    () => getCdmProgrammeOptions(cdmStatus?.allowedActions),
    [cdmStatus?.allowedActions]
  );

  const isPatientValid = !!pcrsSchemeCheck?.validPatient;
  // Keep the skeleton up until BOTH PCRS responses are settled: the scheme
  // check and — when genuinely expected — the cdm-ws patient details. Details
  // that are not expected (private / no-GMS / no-credentials) or that have
  // errored are not "pending", so those paths never wait and never hang.
  const isContentReady =
    !!doctor && !!options && !isPCRSLoading && !isCdmPatientDetailsPending;
  const currentProgramme = cdmStatus?.currentProgramme;

  // When PCRS returned a details object but omitted a field the review decision
  // depends on (see REQUIRED_PCRS_DETAIL_FIELDS), we cannot proceed: the review
  // is blocked and an error notice lists the missing fields. Only evaluated once
  // details were genuinely expected AND returned — private / no-WS / errored
  // paths yield no details object, so they keep their current behaviour.
  const missingRequiredPcrsDetails = useMemo(
    () =>
      getMissingRequiredPcrsDetailsList({
        isContentReady,
        isPatientValid,
        patientDetails: cdmPatientDetails,
      }),
    [isContentReady, isPatientValid, cdmPatientDetails]
  );
  const isMissingRequiredPcrsDetails = missingRequiredPcrsDetails.length > 0;

  const showMidMonthNotice = useMemo(
    () =>
      getShowMidMonthNotice({
        isContentReady,
        isPatientValid,
        registrationDate: cdmPatientDetails?.registrationDate,
        isMissingRequiredPcrsDetails,
      }),
    [
      isContentReady,
      isPatientValid,
      cdmPatientDetails?.registrationDate,
      isMissingRequiredPcrsDetails,
    ]
  );

  const showNotOnPanelNotice = useMemo(
    () =>
      getShowNotOnPanelNotice({
        isContentReady,
        isPatientValid,
        hasWsConnection,
        patientDetails: cdmPatientDetails,
      }),
    [isContentReady, isPatientValid, hasWsConnection, cdmPatientDetails]
  );

  const areReviewActionsDisabled = useMemo(
    () => getAreReviewActionsDisabled(hasWsConnection),
    [hasWsConnection]
  );

  const isEnrolled = useMemo(
    () => isPatientEnrolled(currentProgramme),
    [currentProgramme]
  );

  return {
    options,
    isPatientValid,
    isContentReady,
    currentProgramme,
    showMidMonthNotice,
    showNotOnPanelNotice,
    missingRequiredPcrsDetails,
    isMissingRequiredPcrsDetails,
    areReviewActionsDisabled,
    isEnrolled,
  };
};
